import React from "react";

import {
  TouchableOpacity,
  StyleSheet,
  View,
  ScrollView,
  Text,
  TextInput,
  Platform,
} from "react-native";

import moment from "moment";
import { find } from "lodash";

import appStyles from "../../../appStyles";
import AppTheme from "../../AppTheme";

import LoadingIndicator from "../../common/LoadingIndicator";
import ManaTextInput from "../../common/ManaTextInput";
import StarRating from "../../common/StarRating/StarRating";

import Toggle from "../../common/Toggle";
import EditGoalFabButton from "./EditGoalFabButton";
import ResolutionInterval from "./ResolutionInterval";
import ResolutionWeekDays from "./ResolutionWeekDays";

import TUTORIALS_DATA from "../../tutorials/data";
import TutorialsHelper from "../../tutorials/TutorialsHelper";
import TimePickerDialog from "../../common/TimePickerDialog";
import { decToHHmm, hhMMToDec, isWeb } from "../../../../helpers/utils";
import Icons from "../../common/icons";
import IntervalPicker from "../../common/interval-dropdown/IntervalPicker";

import ElementItem from "../../elements/list/ElementItem";
import AppMessages from "../../../../config/AppMessages";
import Header from "../../common/header";
import appPages from "../../../../appPages";
import ManaButton from "../../common/ManaButton";
import GoalsHelper from "../GoalsHelper";

export default class ResolutionEdit extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;
    this.state = this.getStateData(props);
  }

  componentDidMount = async () => {
    const showTutorials = (this.controller?.state?.showTutorials || {})[
      TutorialsHelper.STORAGE_KEYS.goals
    ];

    if (showTutorials) {
      this.loadTutorial(0);
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (this.props.goal?.resolution_id !== this.state.goal?.resolution_id) {
      this.setState(this.getStateData(this.props));
    }
  }

  getStateData(props) {
    return {
      tutorialStep: 0,
      showTimePicker: false,
      goal: {
        ...props.goal,
        //from: moment(props.goal?.from).format("YYYY-MM-DD"),
        //to: moment(props.goal?.to).format("YYYY-MM-DD"),
        element: this.getGoalElement(props.goal),
      },
    };
  }

  getGoalElement = (goal) => {
    if (!goal || !goal.id_element) return null;

    const source = this.controller.state?.elements || [];
    const editElement = find(source, (el) => el.id == goal.id_element);
    return editElement;
  };

  // ------------------------------------- SAVE -------------------------------------
  showErrorMessage = () => {
    this.setState((state) => ({
      ...state,
      mariMessage: {
        message: AppMessages.GoalValidationError,
        hasError: true,
      },
    }));

    setTimeout(() => {
      this.setState((state) => ({ ...state, mariMessage: "" }));
    }, 5000);
  };

  isValidModel = () => {
    const { goal } = this.state;
    return goal.operator;
  };

  saveGoal = () => {
    const { goal, showTutorials } = this.state;

    // Do not save if no core element nor a base tag was selected
    if (!this.isValidModel()) {
      return this.showErrorMessage();
    }

    this.setState((state) => ({ ...state, isLoading: true }));
    GoalsHelper.saveGoal(this.controller, goal, () => {
      this.controller.navigate(
        appPages.resolutionPage,
        showTutorials ? { tutorialDone: true } : {},
      );
    });
  };

  updateGoal = (key, value) => {
    if (!key) return;

    const today = moment().format("YYYY-MM-DD");
    if (key === "to" && value < today) {
      value = today;
    }

    const goal = {
      ...this.state.goal,
      [key]: value,
    };

    if (key === "from" && this.state.goal.to && this.state.goal.to < value) {
      goal.to = value;
    }
    if (key === "to" && this.state.goal.from && this.state.goal.from > value) {
      goal.from = value;
    }

    this.setState((state) => ({
      ...state,
      goal: goal,
    }));
  };

  toggleTimePicker = (isOpen) => {
    this.setState((state) => ({ ...state, showTimePicker: isOpen }));
  };

  saveHours = (hours) => {
    this.updateGoal("target_hours", hhMMToDec(hours));
    this.toggleTimePicker(false);
  };

  // ------------------------------------- TUTORIALS -------------------------------------
  loadTutorial = (step = 0, isTutorialEditMode = false) => {
    const { goal } = this.state;

    const tutorial =
      step >= 0 ? TUTORIALS_DATA["resolution_edit"][step] || {} : {};

    if (step == 1 && !goal.target_hours) return;
    if (step == 2 && goal.weekdays === "0000000") return;
    if (step == 3 && (!goal.to || !goal.from)) return;

    this.setState((state) => ({
      ...state,
      showTutorials: true,
      tutorialStep: step,
      isTutorialEditMode,
      mariMessage: tutorial.message,
      mariAction: tutorial.action
        ? {
            text: tutorial.action.text,
            onPress: () => this.loadTutorial(step + 1),
          }
        : {},
    }));
  };

  getContainerStyle = (containerIndex) => {
    const { tutorialStep, showTutorials } = this.state;
    if (!showTutorials || tutorialStep == containerIndex) return {};

    return {
      opacity: 0.2,
      pointerEvents: "none",
    };
  };

  goBack = () => {
    const { showTutorials, tutorialStep } = this.state;
    if (!showTutorials || tutorialStep == 0) {
      this.controller.goBack();
    }

    this.loadTutorial(tutorialStep - 1, true);
  };

  onPressElement = () => {
    if (this.props.onPressElement != null) {
      this.props.onPressElement();
    }
  };

  render() {
    const {
      isLoading,
      goal,
      mariMessage,
      showTutorials,
      mariAction,
      tutorialStep,
      showTimePicker,
      isTutorialEditMode,
    } = this.state;

    return (
      <View style={[appStyles.container, { backgroundColor: "#F3F2F1" }]}>
        <Header
          title={goal.resolution_id ? "Edit Goal" : "New Goal"}
          subTitle={"Enter some details"}
          controller={this.controller}
          returnPage="rn:resolution"
          noAvatarAction
          goBack={this.goBack}
        />
        {isLoading ? <LoadingIndicator isLoading={isLoading} /> : null}
        <ScrollView
          style={[appStyles.scrollView]}
          contentContainerStyle={[
            appStyles.scrollViewContent,
            {
              paddingVertical: 2,
              backgroundColor: "#FFF",
              paddingBottom: 350,
            },
          ]}
        >
          <View style={styles.group}>
            {/* Element */}
            {goal.element ? (
              <TouchableOpacity onPress={this.onPressElement}>
                <View
                  style={[
                    styles.rowContainer,
                    styles.borderBotttom,
                    { paddingVertical: 2, paddingLeft: 2 },
                  ]}
                >
                  <View
                    style={{
                      overflow: "hidden",
                      flex: 1,
                      maxWidth: "95%",
                    }}
                  >
                    <ElementItem
                      item={goal.element}
                      controller={this.controller}
                      noIcons={true}
                      hideActions={true}
                      disabledHighlight={true}
                      onElementSelected={this.onPressElement}
                    />
                  </View>
                </View>
              </TouchableOpacity>
            ) : null}

            {/* Operator */}
            <View
              style={[
                styles.rowContainer,
                styles.borderBotttom,
                this.getContainerStyle(0),
              ]}
            >
              <Toggle
                style={{ minWidth: isWeb ? 270 : 250 }}
                selected={goal.operator}
                options={["less than", "exactly", "more than"]}
                disabled={isTutorialEditMode ? ["less than"] : []}
                onToggle={(operator) => this.updateGoal("operator", operator)}
              />

              <View style={styles.hoursContainer}>
                <TextInput
                  name={"resolutionEdit"}
                  keyboardType="numeric"
                  style={styles.hoursInput}
                  placeholderTextColor="#9C9EB9"
                  onFocus={() => this.toggleTimePicker(true)}
                  value={decToHHmm(goal.target_hours)}
                />
              </View>
            </View>

            {/* Time unit */}
            <View
              style={[
                styles.rowContainer,
                styles.borderBotttom,
                this.getContainerStyle(0),
                { zIndex: 100, overflow: "visible" },
              ]}
            >
              <Text style={styles.miniLabel}>Every</Text>

              <IntervalPicker
                defaultValue={goal.time_unit}
                textStyle={styles.dropdownText}
                onPickerValueChanged={(value) =>
                  this.updateGoal("time_unit", value)
                }
              />
            </View>

            {/* Week days */}
            <View
              style={[
                styles.rowContainer,
                styles.borderBotttom,
                this.getContainerStyle(1),
              ]}
            >
              <Text style={styles.miniLabel}>On</Text>

              <ResolutionWeekDays
                weekdays={goal.weekdays}
                onSelectedChange={(value) => this.updateGoal("weekdays", value)}
              />
            </View>

            {/* Start */}
            <View
              style={[
                styles.dateContainer,
                styles.borderBotttom,
                this.getContainerStyle(2),
              ]}
            >
              <ResolutionInterval
                label="Starts"
                isStartDate
                goal={goal}
                onDateChanged={this.updateGoal}
                controller={this.controller}
              />
            </View>

            {/* End */}
            <View
              style={[
                styles.dateContainer,
                styles.borderBotttom,
                this.getContainerStyle(2),
              ]}
            >
              <ResolutionInterval
                label="Ends"
                goal={goal}
                onDateChanged={this.updateGoal}
                controller={this.controller}
              />
            </View>
          </View>

          <View style={[styles.group, this.getContainerStyle(3)]}>
            {/* Why */}
            <View style={[styles.textContainer, styles.borderBotttom]}>
              <View style={styles.rowInput}>
                <Icons.Favorite
                  color={AppTheme.almostBlack}
                  style={styles.icon}
                  width={20}
                  height={20}
                />
                <Text style={styles.label}>Why is this important to you?</Text>
              </View>

              <View style={[styles.inputContainer]}>
                <ManaTextInput
                  name={"resolutionEditNote"}
                  style={[styles.input]}
                  placeholderTextColor="#676C78"
                  placeholder="Write some notes about why you’re making this a priority"
                  onTextChanged={(text) => this.updateGoal("description", text)}
                  value={goal.description || ""}
                  multiline
                />
              </View>
            </View>

            {/* How */}
            <View style={[styles.textContainer, styles.borderBotttom]}>
              <View style={styles.rowInput}>
                <Icons.HowIcon style={styles.icon} />
                <Text style={styles.label}>
                  How are you going to achieve this?
                </Text>
              </View>

              <View style={[styles.inputContainer]}>
                <ManaTextInput
                  name={"resolutionHowNote"}
                  style={[styles.input]}
                  placeholderTextColor="#676C78"
                  placeholder="Write some concrete steps you can take to make it happen"
                  onTextChanged={(text) => this.updateGoal("how", text)}
                  value={goal.how || ""}
                  multiline
                />
              </View>
            </View>

            {/* Importance */}
            <View style={[styles.importanceContainer, styles.borderBotttom]}>
              <Text style={styles.miniLabel}>Importance Rating</Text>
              <StarRating
                rating={goal.importance}
                starSize={21}
                onPress={(importance) =>
                  this.updateGoal("importance", importance)
                }
              />
            </View>
          </View>
        </ScrollView>

        {!showTutorials || (showTutorials && tutorialStep == 4) ? (
          <View style={appStyles.footerButtons}>
            <ManaButton
              text={goal.resolution_id ? "Update Goal" : "Create Goal"}
              onPress={this.saveGoal}
              style={[tutorialStep == 4 ? appStyles.highlightBorder : {}]}
            />
          </View>
        ) : null}

        <TimePickerDialog
          value={decToHHmm(goal.target_hours)}
          isOpen={showTimePicker}
          onConfirm={(value) => this.saveHours(value)}
          onClose={() => this.toggleTimePicker(false)}
          use24hFormat
          pickDuration
        />
        <EditGoalFabButton
          mariOptions={{
            isLoading,
            mariMessage,
            mariAction: [mariAction],
            showTutorials,
            hideActions: showTutorials,
          }}
          style={
            !showTutorials || tutorialStep == 4 ? { bottom: 70 } : { bottom: 0 }
          }
        />
      </View>
    );
  }
}

const styles = StyleSheet.create({
  group: {
    backgroundColor: "#FFF",
    borderRadius: 4,
    marginBottom: 8,
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "row",
  },
  rowInput: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    paddingHorizontal: 8,
    paddingTop: 4,
  },
  borderBotttom: {
    borderBottomColor: "#f5f5f5",
    borderBottomWidth: 1,
  },
  rowContainer: {
    height: 40,
    paddingHorizontal: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "row",
  },
  dateContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "row",
    paddingHorizontal: 12,
    minHeight: 40,
  },
  miniLabel: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-Regular",
    }),
    fontWeight: "300",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.5,
    color: AppTheme.almostBlack,
    flex: 1,
  },
  label: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    textAlign: "center",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.175,
    color: AppTheme.almostBlack,
  },
  hoursContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "row",
  },
  hoursInput: {
    width: 80,
    textAlign: "center",
    borderBottomWidth: 1,

    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 19,
    lineHeight: 29,
    letterSpacing: 0.972222,
    color: "#2D3142",
    paddingBottom: 5,
  },
  textContainer: {
    width: "100%",
    display: "flex",
    paddingBottom: 9,
    minHeight: 125,
  },
  inputContainer: {
    paddingHorizontal: 36,
  },
  input: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-Regular",
    }),
    fontWeight: "300",
    width: "100%",
    marginBottom: 10,
    paddingHorizontal: 15,
    paddingTop: 10,
    borderRadius: 4,
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.77,
    elevation: 3,
    color: AppTheme.almostBlack,
    backgroundColor: "#F3F2F1",
    minHeight: 92,
    shadowOpacity: 0,
  },
  importanceContainer: {
    height: 44,
    paddingHorizontal: 12,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "row",
  },
  icon: {
    width: 24,
    height: 24,
    marginRight: 4,
  },
  dropdownText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.175,
    color: AppTheme.almostBlack,
    textAlign: "right",
  },
});
